import {isBrowser} from './isBrowser';
import {isLinkUrl} from './isLinkUrl';
import {isOnelinkUrl} from './isOnelinkUrl';

const DEVBOX_MATCHER = '-mydev.dev.stripe.me';
// https://go/pay-tunnel-legacy
const TUNNEL_YARN_MATCHER = '-stripejs.tunnel.stripe.me';
// https://go/pay-tunnel
const TUNNEL_MKT_SRV_MATCHER = '-mkt.tunnel.stripe.me';

const QA_STRIPE_COM_MATCHERS = [
  /(^|\.)qa\.stripe\.com$/,
  /(^|\.)(link|admin|docs)\.qa\.corp\.stripe\.com$/,
  /(^|\.)qa-dashboard\.stripe\.com$/,
  /(^|\.)qa-docs\.stripe\.com$/,
  /(^|\.)qa-bfcm\.stripe\.com$/,
];

const QA_LINK_CO_MATCHERS = [
  /(^|\.)qa\.link\.co$/,
  /(^|\.)qa-app\.stripe\.co$/,
];

const QA_LINK_COM_MATCHERS = [
  /(^|\.)qa\.link\.com$/,
  /(^|\.)qa-app\.link\.com$/,
];

const PREPROD_STRIPE_COM_MATCHERS = [
  /(^|\.)preprod\.stripe\.com$/,
  /(^|\.)(link|admin|docs)\.preprod\.corp\.stripe\.com$/,
  /(^|\.)preprod-dashboard\.stripe\.com$/,
  /(^|\.)preprod-dashboard\.stripe\.com$/,
  /(^|\.)preprod-docs\.stripe\.com$/,
];

/**
 * The API endpoints to call to change the user's permissions on domains other than stripe.com.
 */
export const TLD_COOKIE_PERMISSION_URLS = {
  // Production TLDs, defined here:
  // https://git.corp.stripe.com/stripe-internal/gocode/blob/1d9f1637b328973bdc1f4efbbd26877a542a1fba/puppet-config/terraform/stripe.io/cookie_compliance/prod/main.tf
  production: [
    'https://c.stripe.dev/cookie',
    'https://c.stripe.global/cookie',
    'https://c.stripe.partners/cookie',
    'https://c.increment.com/cookie',
  ],
  // QA TLDs, defined here:
  // https://git.corp.stripe.com/stripe-internal/gocode/blob/1d9f1637b328973bdc1f4efbbd26877a542a1fba/puppet-config/terraform/stripe.io/cookie_compliance/qa/main.tf
  qa: [
    'https://qa-c.stripe.dev/cookie',
    'https://qa-c.stripe.global/cookie',
    'https://qa-c.stripe.partners/cookie',
    'https://qa-c.increment.com/cookie',
  ],
};

const getBaseUrl = (href: string = isBrowser ? window.location.href : '') => {
  if (isOnelinkUrl(href)) {
    return 'https://onelink.com';
  }

  if (isLinkUrl(href)) {
    if (href.match(/(\.)?link.co([^m]|\b)/i)) {
      return 'https://link.co';
    }

    return 'https://link.com';
  }

  return 'https://stripe.com';
};

/**
 * Returns a URL string that represents the path in the proper environment and domain, given
 * the current URL (href). This correctly returns a path for a devbox, tunnel, qa, preprod,
 * link.co, link.com, and full stack PRR domains, etc.
 * @param path The desired path to visit in the given environment for the given href.
 * @param href The current URL that is used to determine the environment and resulting URL..
 * @returns A URL string that represents the path in the proper environment and domain.
 */
export const resolveUrl = (
  path: string,
  href: string = isBrowser ? window.location.href : '',
) => {
  const endpoint = getBaseUrl(href) + path;

  // URL and URLSearchParams are not supported in older browsers.
  // This check is to protect against referencing unsupported APIs in prod.
  // If they are needed in contexts where they are not supported, we should
  // consider using a polyfill.
  if (!isBrowser || !window.URL || !window.URLSearchParams) {
    return endpoint;
  }
  const url = new URL(href);
  const endpointUrl = new URL(endpoint);

  // If we're running on a devbox or using the stripe js tunnel,
  // we want to call out to the user's instance of mkt-srv instead of to prod.
  if (
    url.hostname.match(DEVBOX_MATCHER) ||
    url.hostname.match(TUNNEL_YARN_MATCHER)
  ) {
    const [host] = url.hostname.split('.', 1);
    let username;
    // If the devbox is remote then it has the pattern #{user}-#{qualifier}--#{service}
    if (host.split('--', 1)[0] !== host) {
      [username] = host.split('--', 1);
      username += '-';
    } else {
      [username] = host.split('-', 1);
    }
    endpointUrl.hostname = `${username}-mkt-mydev.dev.stripe.me`;

    // manage-dashboard-proxy should call prod mkt-srv cookie endpoints
    if (host.includes('manage-dashboard-proxy-mydev')) {
      endpointUrl.hostname = `stripe.com`;
    }

    // link.com and link.co is triggered on mkt-srv via the `$host` query parameter.
    // Ensure we set it.
    if (endpoint.includes('link.co')) {
      endpointUrl.search = new URLSearchParams({$host: 'link'}).toString();
    }
    return endpointUrl.toString();
  } else if (url.hostname.match(/(^|-)(dev|iso|corpiso)\.dev\.stripe\.me$/)) {
    // With fullstack PRR, carry the same base domain
    return endpoint.replace('stripe.com', url.hostname);
  } else if (
    QA_STRIPE_COM_MATCHERS.some((matcher) => matcher.test(url.hostname))
  ) {
    // and similarly in the QA env, we want to use endpoints on the QA server
    return endpoint.replace('stripe.com', 'qa.stripe.com');
  } else if (
    PREPROD_STRIPE_COM_MATCHERS.some((matcher) => matcher.test(url.hostname))
  ) {
    return endpoint.replace('stripe.com', 'preprod.stripe.com');
  } else if (
    QA_LINK_CO_MATCHERS.some((matcher) => matcher.test(url.hostname))
  ) {
    return endpoint.replace('link.co', 'qa.link.co');
  } else if (
    QA_LINK_COM_MATCHERS.some((matcher) => matcher.test(url.hostname))
  ) {
    return endpoint.replace('link.com', 'qa.link.com');
  } else if (
    url.hostname.match(/(^|\.)preprod\.link\.co$/) ||
    url.hostname.match(/(^|\.)preprod-app\.stripe\.co$/)
  ) {
    return endpoint.replace('link.co', 'preprod.link.co');
  } else if (
    url.hostname.match(/(^|\.)preprod\.link\.com$/) ||
    url.hostname.match(/(^|\.)preprod-app\.link\.com$/)
  ) {
    return endpoint.replace('link.com', 'preprod.link.com');
  } else if (
    url.hostname.match(/mkt(\.qa)?\.corp\.stripe\.com/i) ||
    url.hostname.match(TUNNEL_MKT_SRV_MATCHER)
  ) {
    endpointUrl.hostname = url.hostname;

    // Link.co is triggered on mkt-srv via the `$host` query parameter.
    // Ensure we set it.
    if (endpoint.includes('link.co')) {
      endpointUrl.search = new URLSearchParams({$host: 'link'}).toString();
    }
    return endpointUrl.toString();
  }

  return endpointUrl.toString();
};

/**
 * Returns the URLs to call to change the user's permissions on domains other than stripe.com.
 * This method is used to check if we should utilize the QA or the production TLDs.
 * @returns The URLs to call to change the user's permissions on domains other than stripe.com.
 */
export const resolveTLDPermissionUrls = (
  href: string = isBrowser ? window.location.href : '',
) => {
  let host: string;
  try {
    host = new URL(href).hostname;
  } catch (error) {
    return TLD_COOKIE_PERMISSION_URLS.production;
  }

  if (
    [
      DEVBOX_MATCHER,
      TUNNEL_YARN_MATCHER,
      TUNNEL_MKT_SRV_MATCHER,
      ...QA_STRIPE_COM_MATCHERS,
      ...QA_LINK_CO_MATCHERS,
      ...QA_LINK_COM_MATCHERS,
      /(^|-)(dev|iso|corpiso)\.dev\.stripe\.me$/,
      /mkt\.qa\.corp\.stripe\.com/i,
    ].some((matcher) => host.match(matcher))
  ) {
    return TLD_COOKIE_PERMISSION_URLS.qa;
  }

  return TLD_COOKIE_PERMISSION_URLS.production;
};
